import { ReactNode } from "react"
import prettier from "prettier"
import parserTypeScript from "prettier/parser-typescript"
import { RoleType, Capabilities, hasPermission, hasRole, } from './roles'

export { RoleType, Capabilities, hasPermission, hasRole }

export function merge(obj1: any, obj2: any) {
    let key: any
    for (key of Object.keys(obj2)) {
        if (!obj1.hasOwnProperty(key) || typeof obj2[key] !== 'object') obj1[key] = obj2[key]
        else merge(obj1[key] || {}, obj2[key] || {})
    }
    return obj1
}

export const hash = (value: any): string => {
    return JSON.stringify(value, (key, value) => {
        return value
    })
}

export const nl2br = (value: string, asReactNode: boolean = false): string | ReactNode => {
    if (!value) return asReactNode ? null : ''
    if (asReactNode) {
        return value.split("\n").map(function (item: string, index: number) {
            return (<span key={index}>{item}<br /></span>)
        })
    }
    return value.split("\n").join('<br/>')
}

export const call_func = (func: Function, args: any[]) => {
    const count = args.length || 0
    switch (count) {
        case 0: return func()
        case 1: return func(args[0])
        case 2: return func(args[0], args[1])
        case 3: return func(args[0], args[1], args[2])
        case 4: return func(args[0], args[1], args[2], args[3])
        case 5: return func(args[0], args[1], args[2], args[3], args[4])
        case 6: return func(args[0], args[1], args[2], args[3], args[4], args[5])
        case 7: return func(args[0], args[1], args[2], args[3], args[4], args[5], args[6])
        case 8: return func(args[0], args[1], args[2], args[3], args[4], args[5], args[6], args[7])
        case 9: return func(args[0], args[1], args[2], args[3], args[4], args[5], args[6], args[7], args[8])
        case 10: return func(args[0], args[1], args[2], args[3], args[4], args[5], args[6], args[7], args[8], args[9])
    }

    return false
}

export const baseName = (fileName: string, withExtension: boolean = true) => {
    const pattern = /\/?(?:(?:[^\/]+\/)|(?<name>[^.]+)\.?(?<ext>.*)$)/sg

    let m
    while ((m = pattern.exec(fileName)) !== null) {
        // This is necessary to avoid infinite loops with zero-width matches
        if (m.index === pattern.lastIndex) {
            pattern.lastIndex++
        }

        if (m.groups?.name) {
            let name = m.groups.name
            if (m.groups?.ext && withExtension) name += '.' + m.groups.ext
            return name
        }
    }

    return fileName
}

export const getTextWidth = (text: string, font: any = null) => {
    const canvas = document.createElement('canvas');
    const context: any = canvas.getContext('2d');

    context.font = font || getComputedStyle(document.body).font;

    return context.measureText(text).width * 1.2 + 22;
}

export const formatCode = async (code: string): Promise<string> => {
    try {
        return await prettier.format(code, {
            parser: "typescript",
            plugins: [parserTypeScript],

        })
    } catch (err: any) {
        console.error(err)
        return code
    }
};

export const parseQueryArguments = (query: string, args?: IArguments, ignoreArgs: string[] = ['page', 'count', 'q']) => {
    let result = query

    if (args && args.length > 0) {
        for (let arg of args) {
            const keys = Object.keys(arg)
            for (let key of keys) {
                if (ignoreArgs.indexOf(key) !== -1) continue
                const value = arg[key]
                if (typeof value === 'undefined') continue
                if (result) result += '&'
                result += `${key}=${encodeURI(value)}`
            }
        }
    }

    return result
}

/**
 * Waits for the specified number of milliseconds before executing the provided function. If the returned function is called again before the delay has passed, the previous timer is cleared and a new one is set.
 * @param fn The function to be executed after the delay.
 * @param delay The number of milliseconds to wait before executing the function.
 * @returns 
 */
export function debounce<T extends (...args: any[]) => void>(
    fn: T,
    delay: number
) {
    let timer: NodeJS.Timeout;

    return (...args: Parameters<T>) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            fn(...args);
        }, delay);
    };
}

export const isClient = typeof window !== 'undefined'
export const isServer = !isClient